// launch.ts — dice's TRUST-LAYER launch glue (sc-55115, SC-61814). The single-use REAL launch ticket arrives in the URL
// fragment (#t=…); the game shell owns launch (the shared SDK never reads the URL — createGameSDK takes a launchUrl), so
// this stays local. Only bootstrap.ts imports it; game code must not (check-sdk-boundary.mjs).
//
// The money mode comes from the LAUNCH, never from browser storage (launch-decision.ts holds the rule; Plinko ships the
// same one, SC-61834). This module applies it to the document: it takes the ticket, holds #mode=pending until the REAL
// session is ready and #mode=real after (so a reload resumes it), and reboots the page when the launch fragment changes
// under a live document. It no longer reads or writes `blocklabs.rgs.real`: every game on the origin shares that key
// and the SDK rewrites it, which is how a ticketless open resumed REAL and a dead session's reload fell into the demo.

import {
  bareLoadPolicy,
  decideLaunch,
  mayMarkReady,
  REAL_PENDING_FRAGMENT,
  REAL_RESUME_FRAGMENT,
  type LaunchDecision,
} from "./launch-decision";
import { isShellBoot } from "./boot-context";

// VITE_BARE_LOAD_POLICY, injected (and validated) by vite.config.ts; absent in unit tests -> the default.
declare const __DICE_BARE_LOAD_POLICY__: string | undefined;
const configuredBareLoad = typeof __DICE_BARE_LOAD_POLICY__ === "string" ? __DICE_BARE_LOAD_POLICY__ : undefined;

// In rgs-core-api-gw's shell (SC-54713) a bare URL is never a launch: that document is the REAL-capable production path,
// so it asks for a casino launch instead of booting the demo, whatever the page build's own policy says.
const effectiveBareLoad = (): string | undefined => (isShellBoot() ? "launch-required" : configuredBareLoad);

// The fragment this document currently stands for. Our own replaceState calls fire no hashchange, so it is updated
// with every one of them; any OTHER change of the fragment is a new launch (see rebootOnLaunchChange).
let canonicalFragment = "";
let freezeGame: () => void = () => {};
let rebootArmed = false;

// takeLaunch decides the mode from the URL fragment SYNCHRONOUSLY at boot, before any await. A ticket is replaced in
// location.href immediately (history.replaceState) so it cannot linger for a Referer / analytics / error reporter. It is
// replaced by #mode=pending until the REAL session is ready (markRealSession): a reload during a hung or failed exchange
// (the browser's, the loader's Retry) then runs NEITHER mode on any build — never the FUN demo and never an older stored
// session — and asks for a casino launch.
export function takeLaunch(): LaunchDecision {
  const decision = decideLaunch(globalThis.location?.hash ?? "", bareLoadPolicy(effectiveBareLoad()));
  canonicalFragment = globalThis.location?.hash ?? "";
  if (decision.kind === "real-fresh") replaceFragment(REAL_PENDING_FRAGMENT);
  return decision;
}

// markRealSession records, once a REAL session is ready, that this document is a REAL launch: a reload (the browser's,
// the loader's Retry) then resumes REAL instead of guessing. Not a secret — the ticket is gone. Only while the URL still
// stands for THIS launch (mayMarkReady): if a newer navigation replaced the fragment meanwhile (a new #t=, or #mode=fun)
// its hashchange is still queued and must reload — overwriting it would swallow that launch.
export function markRealSession(): void {
  if (!mayMarkReady(globalThis.location?.hash ?? "", canonicalFragment)) return;
  replaceFragment(REAL_RESUME_FRAGMENT);
}

// rebootOnLaunchChange: the mode is decided once, at boot. A fragment-only navigation does not reload the document, so
// the casino reusing this tab for a new launch (a new #t=, or #mode=fun) would otherwise leave the old mode running; and
// a page restored from the back/forward cache comes back with its old in-memory session. Both reload, so a fresh boot
// decides again — but the outgoing document keeps running until the new one commits, so `freeze` stops its game first
// (no round, autobet included, may start in the mode being left). The reload is not the freeze's to prevent: whatever
// the freeze does, the page reloads (a freeze that threw once left the player on a notice the casino's relaunch could
// not get past).
export function rebootOnLaunchChange(freeze: () => void): void {
  freezeGame = freeze;
  if (rebootArmed) return; // one document, one set of listeners
  rebootArmed = true;
  const reboot = (): void => {
    try {
      freezeGame();
    } finally {
      globalThis.location.reload();
    }
  };
  globalThis.addEventListener?.("hashchange", () => {
    if ((globalThis.location?.hash ?? "") !== canonicalFragment) reboot();
  });
  globalThis.addEventListener?.("pageshow", (e: PageTransitionEvent) => {
    if (e.persisted) reboot();
  });
}

// buildLaunchUrl turns a session token into the handoff URL the shared SDK's parseLaunchHandoff expects
// (?sessionToken=<token>&gameId=dice) — the SDK boots a session by parsing this and calling the platform's
// openSession. The URL is a handoff string only, never navigated to. REAL passes the single-use launch ticket
// (the real client exchanges it for a DPoP-bound cookie); FUN passes a fixed placeholder the injected fake
// ignores. Every fresh boot needs a launchUrl — the SDK only skips it to RESTORE a previously-persisted session.
export function buildLaunchUrl(sessionToken: string): string {
  const base = globalThis.location?.href ?? "http://localhost/";
  const url = new URL(base);
  url.hash = "";
  url.search = "";
  url.searchParams.set("sessionToken", sessionToken);
  url.searchParams.set("gameId", "dice");
  return url.toString();
}

// Keep the path, the query (?loader=, the non-prod host token) and the history state; only the fragment changes.
function replaceFragment(fragment: string): void {
  try {
    const { history, location } = globalThis;
    history?.replaceState?.(history.state, "", location.pathname + location.search + fragment);
  } catch {
    /* non-browser or blocked: nothing to rewrite */
  }
  canonicalFragment = globalThis.location?.hash ?? "";
}
